import React, { useState } from 'react';
import { styled } from '@mui/system';
import { Box, Button, Typography } from '@mui/material';

// Define styled components
const Container = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '16px',
  fontFamily: 'Arial, sans-serif',
});

const DateRow = styled(Box)({
  display: 'flex',
  gap: '8px',
});

// DateBox with updated styling
const DateBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isSelected',
})(({ isSelected, theme }) => ({
  border: '1px solid #ccc',
  borderRadius: '8px',
  textAlign: 'center',
  width: '80px',
  height: '100px', // Reduced height
  boxSizing: 'border-box',
  cursor: 'pointer',
  backgroundColor: isSelected ? theme.palette.primary.main : theme.palette.background.paper,
  color: isSelected ? theme.palette.primary.contrastText : theme.palette.text.primary,
  padding: 0, // Remove padding
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
}));

// DateText container
const DateText = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: '100%', // Ensure full width for children
});

// MonthText with smaller font
const MonthText = styled(Typography)({
  fontSize: '14px', // Reduced font size
  lineHeight: '20px',
  padding:'4px 0px',
  fontWeight: '600'
});

// DayNumber with full-width borders
const DayNumber = styled(Typography)(({ isSelected }) => ({
  fontSize: '24px', // Reduced font size
  lineHeight: '28px',
  width: '100%', // Span full width
  padding: '4px 0', // Vertical padding only
  fontWeight: '600',
}));

// DayText with smaller font
const DayText = styled(Typography)({
  fontSize: '14px', // Reduced font size
  lineHeight: '20px',
  padding:'4px 0px',
  fontWeight: '600',
});

const NavButton = styled(Button)({
  padding: '8px',
  fontSize: '24px',
  fontWeight: 'bold',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  minWidth: 'auto',
  '&.Mui-disabled': {
    color: '#ccc',
    cursor: 'not-allowed',
  },
});

const NavButtonContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
});

const SelectedDateText = styled(Typography)({
  marginTop: '16px',
  fontSize: '16px',
  fontWeight: '500',
});

const DateDisplay = () => {
  const today = new Date(); 
  const [startDate, setStartDate] = useState(() => {
    const initialStart = new Date(today);
    initialStart.setDate(today.getDate() - 14); 
    return initialStart;
  });


  const getAllDates = () => {
    const allDates = [];
    const earliestDate = new Date(today);
    earliestDate.setDate(today.getDate() - 89); // 15 days back from today
    for (let i = 0; i < 90; i++) {
      const newDate = new Date(earliestDate);
      newDate.setDate(earliestDate.getDate() + i);
      allDates.push(newDate);
    }
    return allDates;
  };

  // Calculate the index of today in allDates
  const allDates = getAllDates();


  const todayIndex = allDates.findIndex(
    (date) =>
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
  );
  const [selectedDateIndex, setSelectedDateIndex] = useState(todayIndex); // Set to today's index

  // Get dates to display (8 initially, 7 for previous)
  const getDates = (start) => {
    const dates = [];
    //const maxDays = start.getDate() === today.getDate() - 7 ? 8 : 7; // 8 for initial, 7 for prev
    const maxDays = 15;
    for (let i = 0; i < maxDays; i++) {
      const newDate = new Date(start);
      newDate.setDate(start.getDate() + i);
      if (newDate <= today) {
        dates.push(newDate);
      } else {
        break;
      }
    }
    return dates;
  };
 

  const dates = getDates(startDate);

  const handlePrev = () => {
    const newStart = new Date(startDate);
    newStart.setDate(startDate.getDate() - 15); // Go back 5 days
    setStartDate(newStart);
  };

  const handleNext = () => {
    const newStart = new Date(startDate);
    newStart.setDate(startDate.getDate() + 15); // Go forward 5 days
    setStartDate(newStart);
  };

  const handleDateClick = (index) => {
    const clickedDate = dates[index];
    const newIndex = allDates.findIndex(
      (date) =>
        date.getDate() === clickedDate.getDate() &&
        date.getMonth() === clickedDate.getMonth() &&
        date.getFullYear() === clickedDate.getFullYear()
    );
    setSelectedDateIndex(newIndex);
  };

  const isNextDisabled = () => {
    const lastDisplayedDate = dates[dates.length - 1];
    return (
      lastDisplayedDate.getDate() === today.getDate() &&
      lastDisplayedDate.getMonth() === today.getMonth() &&
      lastDisplayedDate.getFullYear() === today.getFullYear()
    );
  };

  const isPrevDisabled = () => {
    const firstDisplayedDate = dates[0];
    const earliestDate = new Date(today);
    earliestDate.setDate(today.getDate() - 89);
    return (
      firstDisplayedDate.getDate() === earliestDate.getDate() &&
      firstDisplayedDate.getMonth() === earliestDate.getMonth() &&
      firstDisplayedDate.getFullYear() === earliestDate.getFullYear()
    );
  };

  const formatSelectedDateRange = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const fromDate = `${year}-${month}-${day} 00:00:00`;
    const toDate = `${year}-${month}-${day} 23:59:59`;
    return { fromDate, toDate };
  };

  const selectedDateRange =
    selectedDateIndex !== -1
      ? formatSelectedDateRange(allDates[selectedDateIndex])
      : null;

  return (
    <Container>
      <NavButtonContainer>
        <NavButton onClick={handlePrev} disabled={isPrevDisabled()}>
          {'<'}
        </NavButton>
        <DateRow>
          {dates.map((date, index) => {
            console.log(date);
            const globalIndex = allDates.findIndex(
              (d) =>
                d.getDate() === date.getDate() &&
                d.getMonth() === date.getMonth() &&
                d.getFullYear() === date.getFullYear()
            );

            const parsedDate = new Date(date);
            const month = parsedDate.toLocaleString('en-US', { month: 'long' }); 
            const dayNumber = parsedDate.getDate(); // e.g., "7"
            const dayName = parsedDate.toLocaleString('en-US', { weekday: 'long' });

            return (
              <DateBox
              key={index}
              isSelected={globalIndex === selectedDateIndex}
              onClick={() => handleDateClick(index)}
            >
              <DateText>
              <MonthText>{month}</MonthText>
              <DayNumber>{dayNumber}</DayNumber>
              <DayText>{dayName}</DayText>
              </DateText>
            </DateBox>
            );
          })}
        </DateRow>
        <NavButton onClick={handleNext} disabled={isNextDisabled()}>
          {'>'}
        </NavButton>
      </NavButtonContainer>

      {selectedDateIndex !== -1 && selectedDateRange && (
        <Box>
          <SelectedDateText>
            From Date: {selectedDateRange.fromDate}
          </SelectedDateText>
          <SelectedDateText>
            To Date: {selectedDateRange.toDate}
          </SelectedDateText>
        </Box>
      )}

    </Container>
  );
};

export default DateDisplay;
